import { uniqBy } from 'lodash-es';

import { BetslipBetBuilderPick } from './picks/betslip-bet-builder-pick';
import { BaseBetslipGroupPick } from './picks/betslip-group-pick';
import { BetslipPick } from './picks/betslip-pick';
import { BetslipUnknownPick } from './picks/betslip-unknown-pick';
import { BetslipV2OptionMarketPick } from './picks/betslip-v2-option-market-pick';
import { BetBuilderPickId, GroupPickId, PickId } from './picks/pick-id';

export function getPicksCount(picks: BetslipPick[]): number {
    return picks.flatMap((pick) => (BaseBetslipGroupPick.isPick(pick) || isUnknownPickWithLegs(pick) ? pick.getLegs() : pick)).length;
}

export function isBetBuilderPick(pick: BetslipPick): pick is BetslipBetBuilderPick | BaseBetslipGroupPick {
    return BetslipBetBuilderPick.isPick(pick) || BaseBetslipGroupPick.isPick(pick);
}

export function isBetslipBetBuilderPick(pick: BetslipPick): pick is BetslipBetBuilderPick {
    return BetslipBetBuilderPick.isPick(pick);
}

export function isBaseBetslipGroupPick(pick: BetslipPick): pick is BaseBetslipGroupPick {
    return BaseBetslipGroupPick.isPick(pick);
}

export function isBetBuilderPickId(pickId: PickId): pickId is BetBuilderPickId | GroupPickId {
    return BetBuilderPickId.isId(pickId) || GroupPickId.isId(pickId);
}

export function getOptionNameFromBetslipPick(pick: BetslipPick) {
    return pick.marketName.name + ' - ' + pick.optionName.name;
}

export function getLegsCount(picks: BetslipPick[]): number {
    const flattenedPicks = uniqBy(flattenGroupPicksIfAny(picks), (pick) => pick.id.toString());

    return flattenedPicks.reduce((acc, curr) => {
        if (BetslipBetBuilderPick.isPick(curr)) {
            return acc + curr.getLegCount();
        }

        return acc + 1;
    }, 0);
}

export function flattenGroupPicksIfAny(picks: BetslipPick[]): BetslipPick[] {
    return picks.flatMap((p) => (BaseBetslipGroupPick.isPick(p) || isUnknownPickWithLegs(p) ? p.getLegs() : p));
}

// Expands the legs for group picks. Keeps the groups in the list. Unique check added in case we're calling this method on a list multiple times
export function flattenPicks(picks: BetslipPick[]): BetslipPick[] {
    return uniqBy(
        picks.flatMap((p) => (BaseBetslipGroupPick.isPick(p) || isUnknownPickWithLegs(p) ? [p, ...p.getLegs()] : p)),
        (pick) => pick.id.toString(),
    );
}

// Checks if picks qualify as valid one pick combo. Currently, 2 cases are supported:
// - Group picks(e.g. Angstrom bet builders)
// - BetBuilder picks(eg. Sportcast) ONLY in case the feature is enabled
export const isValidOnePickCombo = (picks: BetslipPick[], isSportcastAsComboEnabled: boolean) =>
    picks.length === 1 && (BaseBetslipGroupPick.isPick(picks[0]) || (isSportcastAsComboEnabled && BetslipBetBuilderPick.isPick(picks[0])));

// we need the <code>{ legs: { 0: BetslipPick } }</code> restriction because if we have only the <code>pick is BetslipUnknownPick</code>
// then TS will narrow the result type to BetslipUnknownPick, although we expect just a subclass of BetslipUnknownPick
export function isUnknownPickWithLegs(pick: BetslipPick): pick is BetslipUnknownPick & { legs: { 0: BetslipPick } } {
    return BetslipUnknownPick.isPick(pick) && pick.legs.length > 0;
}

export function getOptionIsDraw(pick: BetslipPick): boolean | undefined {
    return pick instanceof BetslipV2OptionMarketPick ? (pick as BetslipV2OptionMarketPick).option.isDraw : undefined;
}

export type ActionSource = 'local' | 'remote';

export enum SourceOfAction {
    Local = 'local',
    Remote = 'remote',
}
